import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Directive, Host, Input, Optional, Self } from "@angular/core";
import { Menu } from "@bfx/primeng/menu";
import { NativeDesktopSupport } from "./native-desktop-support";

import { DOM_HANDLER_MODE_TOKEN, DomHandlerMode, DomHandlerService } from "@bfx/primeng/dom";

import { Observable } from "rxjs";
import { BfxPopupNotificationComponent } from "../bfx-notifications/bfx-popup-notification.component";
import { ResizeObserverScheduler } from "../../shared/helper-services/resize-observer-scheduler";
import { NativeDesktopWindow } from "../../shared/native-desktop/NativeDesktopWindow";
import { Rect } from "shared/native-desktop/NativeDesktopData";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";

const ELEMENT_TITLE: string = "titleElement";

@Directive({
  selector: "[nativeDesktopMenu]",
  providers: [
    {
      provide: DOM_HANDLER_MODE_TOKEN,
      useFactory: () => {
        if (NativeDesktopSupport.isNativeDesktopContainer())
          return new DomHandlerService(DomHandlerMode.NativeContainer);
        return new DomHandlerService(DomHandlerMode.Browser);
      },
    },
  ],
  standalone: false,
})
export class NativeDesktopMenu {
  private static instanceMap: { [key: string]: NativeDesktopWindow } = {};
  private static instanceBlurredHandlerMap: Map<string, (_: fin.WindowBaseEvent) => void> =
    new Map();

  private static readonly instanceCloseOnBlurredMap = new Map([
    ["notifications", true],
    ["detailFill", true],
  ]);
  private static readonly instanceForegroundOnResizeMap = new Map([
    ["notifications", true],
    ["detailFill", true],
  ]);

  private menuId: string = null;

  // not used by default see nativeDesktopParentWindow
  private _parentGroup$: Observable<{
    popup: BfxPopupNotificationComponent;
    window: fin.OpenFinWindow;
  }> = null;
  // private _parentPopup: BfxPopupNotificationComponent;

  private _parentWindow: fin.OpenFinWindow;

  private resizeObserver: ResizeObserver = new ResizeObserverScheduler(
    (entries: ResizeObserverEntry[]) => this.onResize(entries),
    this.constructor.name,
  );

  constructor(@Host() @Self() @Optional() component: Menu) {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.initialize(component);
    }
  }

  private initialize(component: Menu): void {
    if (component instanceof Menu) {
      let menu: any = component as Menu;

      // FIXME: NG8
      let toggle = menu.toggle.bind(menu);
      menu.toggle = (($event) => {
        if (!menu._setup) {
          menu.onOverlayAnimationStart = this.onOverlayAnimationStart.bind(this, menu);
          menu.onShow.subscribe(this.onShow.bind(this, menu));
          menu._setup = true;
        }

        toggle($event);
      }).bind(this);

      let oldOnDestroy = menu.ngOnDestroy.bind(menu);
      menu.ngOnDestroy = (() => {
        try {
          this.onHide(menu);
          oldOnDestroy();
        } catch (e) {
          logger.out.warn("Error closing menu: ", e);
        }
      }).bind(this);
    }
  }

  private onOverlayAnimationStart(menu: Menu, event: any): void {
    switch (event.toState) {
      case "visible":
        if (menu.popup) {
          menu.container = event.element;
          menu.moveOnTop();
          menu.onShow.emit({});

          //this.appendOverlay();
          //DomHandler.absolutePosition(this.container, this.target);
          //this.bindDocumentClickListener();
          //this.bindDocumentResizeListener();
        }
        break;

      case "void":
        this.onHide(menu);
        break;
    }
  }

  public onShow(menu: Menu, event: MouseEvent): void {
    let window: NativeDesktopWindow = NativeDesktopMenu.instanceMap[this.menuId];

    let nativeElement = menu.container;

    let width: number = nativeElement.offsetWidth * NativeDesktopSupport.zoomLevel;
    let height: number = nativeElement.offsetHeight * NativeDesktopSupport.zoomLevel;

    nativeElement.style.opacity = "1";
    nativeElement.style.display = "block";
    nativeElement.style.left = nativeElement.style.top = "0px";
    nativeElement.style.padding = "0";

    if (width == 0) {
      width = window.getNativeWindow().innerWidth;
    }

    if (height == 0) {
      height = window.getNativeWindow().innerHeight;
    }

    if (NativeDesktopMenu.instanceCloseOnBlurredMap.has(this.menuId)) {
      this.resizeObserver.disconnect();
      // observer() only after show
    } else {
      this.resizeObserver.disconnect();
      // this.resizeObserver.observe(menu.container);
    }

    window.getNativeWindow().document.body.appendChild(nativeElement);

    let monitorInfo = NativeDesktopSupport.getMonitorInfo();
    let target: Element = menu.target as Element;
    let boundingRect = target.getBoundingClientRect();

    let x: number =
      target.ownerDocument.defaultView.screenLeft +
      (boundingRect.left + boundingRect.width) * NativeDesktopSupport.zoomLevel -
      width;
    let y: number =
      target.ownerDocument.defaultView.screenTop +
      (boundingRect.top + boundingRect.height) * NativeDesktopSupport.zoomLevel;

    let rect: Rect = monitorInfo.virtualScreen;

    if (y + height > rect.bottom) {
      y = rect.bottom - height;
    }

    if (x + width > rect.right) {
      x = rect.right - width;
    }

    window.setBounds(x, y, width, height).then(() => {
      window.show().then(() => {
        nativeElement.style.opacity = "1";
        if (this._parentWindow != null) {
          window.restoreToFront().then(() => {
            let parentWindowRef = fin.Window.wrapSync({
              uuid: this._parentWindow.uuid,
              name: this._parentWindow.name,
            });
            parentWindowRef["prependOnceListener"]("bounds-changed", this.onHide.bind(this));
            // this.resizeObserver.observe(menu.container);
          });
        } else {
          // this.resizeObserver.observe(menu.container);
        }
      });
    });
  }

  public onHide(menu: Menu): void {
    if (menu != null && menu.container != null && menu.container instanceof Element) {
      this.resizeObserver.unobserve(menu.container);
    }

    if (
      NativeDesktopMenu.instanceBlurredHandlerMap.has(this.menuId) &&
      NativeDesktopMenu.instanceCloseOnBlurredMap.has(this.menuId)
    ) {
      let wnd = NativeDesktopMenu.instanceMap[this.menuId];
      let ref = NativeDesktopMenu.instanceBlurredHandlerMap.get(this.menuId);
      // TODO ELECTRON
      // wnd.removeEventListener("blurred", ref);
      NativeDesktopMenu.instanceBlurredHandlerMap.delete(this.menuId);
    }

    if (NativeDesktopMenu.instanceMap[this.menuId] != null) {
      NativeDesktopMenu.instanceMap[this.menuId].hide();
    }
  }

  public onResize(entries: ResizeObserverEntry[]): Promise<void> {
    return new Promise<void>((all) => {
      let resizePromises: Promise<void>[] = [];

      if (!Array.isArray(entries) || !entries.length) {
        logger.out.error("ResizeObserver: bailing out of callback due to no entries %O", entries);
        return all();
      }
      // TODO ELECTRON (do we need this?)
      // for (const entry of entries) {
      //   const { width, height } = entry.contentRect;
      //   let window = NativeDesktopMenu.instanceMap[this.menuId];

      //   if (window == null || width == 0 || height == 0) {
      //     continue;
      //   }
      //   resizePromises.push(
      //     new Promise<void>((res) => {
      //       let w: number = NativeDesktopSupport.zoomLevel * width;
      //       let h: number = NativeDesktopSupport.zoomLevel * height;
      //       const resError = (err) => {
      //         logger.out.error(`${this.constructor.name} err:${err}`);
      //         res();
      //       };
      //       window.setSize(w, h).then(() => {
      //         if (NativeDesktopMenu.instanceForegroundOnResizeMap.has(this.menuId)) {
      //           // TODO Establish for openfin stack?
      //           // "Brings the window to the front of the OpenFin window stack."
      //           window
      //             .restoreToFront()
      //             .then(() => {
      //               // TODO Establish if this is for win32 stack?
      //               // "Brings the window to the front of the entire window stack."
      //               window.setAsForeground().then(() => {
      //                 window.focus().then(res).catch(resError);
      //               });
      //             })
      //             .catch(resError);
      //         } else if (NativeDesktopMenu.instanceCloseOnBlurredMap.has(this.menuId)) {
      //           window.focus().then(res).catch(resError);
      //         }
      //         // resize done
      //       }, resError);
      //     }),
      //   );
      // }

      Promise.all(resizePromises).then(() => all());
    });
  }

  @Input("nativeDesktopMenu")
  public set location(menuId: string) {
    this.menuId = menuId;
    let title: string = "contextMenu-" + menuId;
    if (
      NativeDesktopSupport.isNativeDesktopContainer() &&
      NativeDesktopMenu.instanceMap[this.menuId] == null
    ) {
      NativeDesktopSupport.openWindow(
        title,
        WindowType.Utility,
        true,
        null,
        (window: NativeDesktopWindow) => {
          let titleElement = window[ELEMENT_TITLE]();
          if (titleElement != null) {
            titleElement.textContent = title;
            titleElement.dataset.nativeDesktopMenu = menuId;
          } else {
            logger.out.error(`${this.constructor.name} failed to set window title: ${title}`);
          }

          NativeDesktopMenu.instanceMap[this.menuId] = window;
          window.close = () => {
            let titleElement = window[ELEMENT_TITLE]();
            if (titleElement != null) {
              try {
                delete titleElement.dataset.nativeDesktopMenu;
              } catch (e) {
                logger.out.error(
                  `${this.constructor.name} failed to set cleanup nativeDesktopMenu ${menuId}`,
                );
              }
            }
          };
        },
      );
    }
  }

  @Input("nativeDesktopParentWindow")
  public set parentGroupElement(
    parentGroup$:
      | Observable<{
          popup: BfxPopupNotificationComponent;
          window: fin.OpenFinWindow;
        }>
      | boolean,
  ) {
    if (parentGroup$ === true) {
      return;
    } else if (parentGroup$ === false) {
      //
      logger.out.warn("template error");
      return;
    }

    this._parentGroup$ = parentGroup$;
    parentGroup$.subscribe(
      (value) => {
        // this._parentPopup = value.popup;
        this._parentWindow = value.window;
      },
      () => {},
      () => {
        // trigger a hide
        if (this._parentWindow && NativeDesktopMenu.instanceMap[this.menuId] != null) {
          NativeDesktopMenu.instanceMap[this.menuId].hide();
        }
      },
    );
  }
}
